import { TitleBar } from '@shopify/app-bridge-react';
import {
  Banner,
  Button,
  Checkbox,
  Collapsible,
  Combobox,
  ExceptionList,
  Icon,
  InlineCode,
  Layout,
  LegacyCard,
  LegacyStack,
  Listbox,
  Page,
  RadioButton,
  Select,
  Spinner,
  Tag,
  Text,
  TextContainer,
  TextField,
} from '@shopify/polaris';
import {
  AlertCircleIcon,
  AlertTriangleIcon,
  ChevronLeftIcon,
  ProductIcon,
  SearchIcon,
  SearchRecentIcon,
} from '@shopify/polaris-icons';
import { ethers } from 'ethers';
import { FormikErrors, useFormik } from 'formik';
import { useCallback, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useHistory, useParams } from 'react-router-dom';
import { getStorefrontsForNetwork } from '@manifoldxyz/shared-storefront-contracts';
import { useCampaignAPI } from '../../../hooks/useCampaignAPI';
import { useFetchCampaign } from '../../../hooks/useFetchCampaign';
import { useFetchCampaignShopifyProducts } from '../../../hooks/useFetchCampaignShopifyProducts';
import { useFetchCampaignTokenNFTAttributes } from '../../../hooks/useFetchCampaignTokenNFTAttributes';
import { useQueryParams } from '../../../hooks/useQueryParams';
import { useRefreshTokenMetadataMutation } from '../../../hooks/useRefreshTokenMetadataMutation';
import { formatRange, SAME_AS_GATE } from '../../../utils/discountLimits';
import {
  CampaignShopifyRule,
  CampaignShopifyRuleRestrictionType,
  DiscountType,
  TokenFilterType,
} from '../../../utils/interfaces/campaign';
import { formatTokenList, MAX_TOKEN_LIST_IDS, parseTokenIdList } from '../../../utils/tokenList';
import { SkeletonShopifyCampaign } from '../components/campaign/SkeletonShopifyCampaign';
import { ShopifyDateTimePicker } from '../components/ShopifyDateTimePicker';
import { useShopifyToastMessage } from '../hooks/useShopifyToastMessage';

/** 'id_list' exists only on discount rules (not campaign allowlist tokens). */
type RuleFilterType = TokenFilterType | 'id_list';

type ManageRulesFormValues = {
  filterType: RuleFilterType;
  /** 'id_list': token ids as typed, comma/space separated. */
  tokenIdList: string;
  network: string; // Needs to be a string because selector dropdown requires string value
  contractAddress: string;
  tokenId: string;
  minTokenId: string;
  maxTokenId: string;
  searchAttributesInput: string;
  selectedProductId: string;
  selectedNftAttributes: Set<string>;
  discountValue: number;
  discountType: DiscountType;
  /** Optional per-discount cap. Empty = no cap of its own. */
  /** 'Set a cap for this discount' ticked. Unticked = no cap (sent as null). */
  capEnabled: boolean;
  maxRedemptions: string;
  /** 'gate' = follow the product gate's dates (stored as null); 'custom' = own dates. */
  datesMode: 'gate' | 'custom';
  customStart: number;
  customEnd: number;
};

/** Pre-fill the form from a saved discount (edit mode). */
function ruleToFormValues(rule: CampaignShopifyRule, productId: string): ManageRulesFormValues {
  const r = rule as any;
  const isAmount = Boolean(r.discountAmount) && !r.discountPercent;
  const filterType: RuleFilterType =
    rule.restrictionType === CampaignShopifyRuleRestrictionType.INDIVIDUAL_TOKEN
      ? 'id'
      : rule.restrictionType === CampaignShopifyRuleRestrictionType.TOKEN_LIST
      ? 'id_list'
      : rule.restrictionType === CampaignShopifyRuleRestrictionType.TOKEN_RANGE
      ? 'id_range'
      : rule.restrictionType === CampaignShopifyRuleRestrictionType.TOKEN_ATTRIBUTE
      ? 'attributes'
      : 'no_filter';
  const hasCustomDates = Boolean(r.startDate || r.endDate);
  return {
    filterType,
    network: String(r.restriction?.network ?? 1),
    contractAddress: r.restriction?.tokenAddress ?? '',
    tokenId: r.restriction?.tokenId ?? '',
    tokenIdList: (r.restriction?.tokenIds ?? []).join(', '),
    minTokenId: r.restriction?.minTokenId ?? '',
    maxTokenId: r.restriction?.maxTokenId ?? '',
    searchAttributesInput: '',
    selectedNftAttributes: new Set<string>(
      (r.restriction?.attributeEntries ?? []).map(
        (a: { traitType: string; value: string }) => `${a.traitType}_${a.value}`
      )
    ),
    selectedProductId: productId,
    discountValue: isAmount ? r.discountAmount : r.discountPercent ?? 0,
    discountType: isAmount ? DiscountType.Amount : DiscountType.Percent,
    capEnabled: Boolean(r.maxRedemptions),
    maxRedemptions: r.maxRedemptions ? String(r.maxRedemptions) : '',
    datesMode: hasCustomDates ? 'custom' : 'gate',
    customStart: r.startDate ? new Date(r.startDate).getTime() : 0,
    customEnd: r.endDate ? new Date(r.endDate).getTime() : 0,
  };
}

export const ShopifyCampaignRuleManager = (): JSX.Element => {
  const { campaignId, appName } = useParams<{ campaignId: string; appName: string }>();
  const { campaign, isLoading } = useFetchCampaign(campaignId);
  const { campaignAPI } = useCampaignAPI();
  const { shopifyProducts, isLoading: isShopifyProductsLoading } = useFetchCampaignShopifyProducts(
    parseInt(campaignId)
  );
  const { setToastMessage } = useShopifyToastMessage();
  const queryClient = useQueryClient();
  const history = useHistory();

  // Edit mode: /rules?edit=<ruleId>&product=<productId>. The server matches an
  // edit to the existing discount by its holder requirement and updates the
  // amount, cap and dates in place (same id, so units already sold still count).
  const queryParams = useQueryParams();
  const editRuleId = queryParams.get('edit');
  const editProductId = queryParams.get('product');
  const editingRule: CampaignShopifyRule | undefined =
    editRuleId && editProductId
      ? (campaign?.rules?.[editProductId] ?? []).find((x) => x.id === editRuleId)
      : undefined;
  const isEditing = Boolean(editingRule);

  const networks = [
    {
      name: 'ethereum',
      value: '1',
    },
    {
      name: 'sepolia',
      value: '11155111',
    },
    {
      name: 'optimism',
      value: '10',
    },
    {
      name: 'base',
      value: '8453',
    },
  ];

  // The product gate's own window; a discount's custom dates must overlap it.
  const gateStart = campaign?.startDate ? new Date(campaign.startDate).getTime() : 0;
  const gateEnd = campaign?.endDate ? new Date(campaign.endDate).getTime() : 0;

  const changeDiscountType = (discount: DiscountType) => {
    formik.setFieldValue('discountType', discount);
    formik.setFieldTouched('discountType');
  };

  const formik = useFormik<ManageRulesFormValues>({
    enableReinitialize: true,
    initialValues: editingRule
      ? ruleToFormValues(editingRule, editProductId!)
      : {
          filterType: 'no_filter',
          network: '1',
          contractAddress: '',
          tokenId: '',
          tokenIdList: '',
          minTokenId: '',
          maxTokenId: '',
          searchAttributesInput: '',
          selectedNftAttributes: new Set<string>(),
          selectedProductId: '',
          discountValue: 10,
          discountType: DiscountType.Percent,
          capEnabled: false,
          maxRedemptions: '',
          datesMode: 'gate',
          customStart: 0,
          customEnd: 0,
        },
    validateOnBlur: false,
    validate: (values) => {
      const errors: FormikErrors<Partial<ManageRulesFormValues>> = {};
      const selectedProductId =
        shopifyProducts.length === 1 ? shopifyProducts[0].productId : values.selectedProductId;
      if (!selectedProductId) {
        errors.selectedProductId = 'Product is required';
      }

      // discount validation
      if (
        (!values.discountValue && values.discountValue !== 0) ||
        Number.isNaN(values.discountValue)
      ) {
        errors.discountValue = 'Required';
      } else {
        switch (values.discountType) {
          case DiscountType.Percent:
            if (values.discountValue > 100 || values.discountValue < 0) {
              errors.discountValue = 'Must be between 0 and 100';
            } else if (values.discountValue.toString().includes('.')) {
              errors.discountValue = 'Must be an integer value';
            }
            break;
          case DiscountType.Amount:
            if (values.discountValue < 0) {
              errors.discountValue = 'Must be greater or equal to 0';
            }
            break;
        }
      }

      if (values.capEnabled) {
        const cap = Number(values.maxRedemptions);
        if (values.maxRedemptions.trim() === '' || !Number.isInteger(cap) || cap < 1) {
          errors.maxRedemptions = 'Enter a whole number of 1 or more';
        }
      }

      if (values.datesMode === 'custom') {
        if (!values.customStart) errors.customStart = 'Pick a start';
        if (!values.customEnd) errors.customEnd = 'Pick an end';
        if (values.customStart && values.customEnd && values.customEnd <= values.customStart) {
          errors.customEnd = 'End must be after start';
        }
        if (gateEnd && values.customStart && values.customStart >= gateEnd) {
          errors.customStart = 'Must start before the product gate ends';
        }
        if (gateStart && values.customEnd && values.customEnd <= gateStart) {
          errors.customEnd = 'Must end after the product gate starts';
        }
      }

      if (!values.contractAddress) {
        errors.contractAddress = 'Contract address is required';
      } else if (!ethers.utils.isAddress(values.contractAddress)) {
        errors.contractAddress = 'Must be valid address';
      }

      // if current value is a shared contract AND filter type is one of none/attributes
      if (
        isSharedContract(parseInt(values.network), values.contractAddress) &&
        (values.filterType === 'no_filter' ||
          values.filterType === 'id_range' ||
          values.filterType === 'attributes')
      ) {
        errors.filterType = 'Invalid filter type for shared storefront contract';
      }

      switch (values.filterType) {
        case 'id_range':
          if (!values.minTokenId) {
            errors.minTokenId = 'Minimum Token ID required';
          }

          if (!values.maxTokenId) {
            errors.maxTokenId = 'Maximum Token ID required';
          }
          break;
        case 'attributes':
          if (values.selectedNftAttributes.size <= 0) {
            errors.searchAttributesInput = 'Requires at least 1 attribute';
          }
          break;
        case 'id_list': {
          const { ids, invalid } = parseTokenIdList(values.tokenIdList);
          if (invalid.length) {
            errors.tokenIdList = `Not a token ID: ${invalid.join(', ')}`;
          } else if (ids.length === 0) {
            errors.tokenIdList = 'Enter at least one token ID';
          } else if (ids.length > MAX_TOKEN_LIST_IDS) {
            errors.tokenIdList = `At most ${MAX_TOKEN_LIST_IDS} token IDs — use a token ID range instead`;
          }
          break;
        }
        case 'id':
          if (!values.tokenId) {
            errors.tokenId = 'Token ID is required';
          }
          // rely on backend for verification
          break;
      }

      return errors;
    },
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true);
      const selectedProductId =
        shopifyProducts.length === 1
          ? shopifyProducts[0].productId
          : formik.values.selectedProductId;
      let rule: Partial<CampaignShopifyRule> = {};
      // Need to parseInt because for some reason the network is
      // converted to a string above, probably when we toString()
      // it for display
      const network = parseInt(values.network);
      switch (values.filterType) {
        case 'no_filter':
          rule = {
            restrictionType: CampaignShopifyRuleRestrictionType.TOKEN,
            restriction: {
              network,
              tokenAddress: values.contractAddress,
            },
          };
          break;
        case 'id_range':
          rule = {
            restrictionType: CampaignShopifyRuleRestrictionType.TOKEN_RANGE,
            restriction: {
              network,
              tokenAddress: values.contractAddress,
              minTokenId: values.minTokenId,
              maxTokenId: values.maxTokenId,
            },
          };

          break;
        case 'id_list':
          rule = {
            restrictionType: CampaignShopifyRuleRestrictionType.TOKEN_LIST,
            restriction: {
              network,
              tokenAddress: values.contractAddress,
              tokenIds: parseTokenIdList(values.tokenIdList).ids,
            },
          };
          break;
        case 'id':
          rule = {
            restrictionType: CampaignShopifyRuleRestrictionType.INDIVIDUAL_TOKEN,
            restriction: {
              network,
              tokenAddress: values.contractAddress,
              tokenId: values.tokenId,
            },
          };

          break;
        case 'attributes':
          rule = {
            restrictionType: CampaignShopifyRuleRestrictionType.TOKEN_ATTRIBUTE,
            restriction: {
              network,
              tokenAddress: values.contractAddress,
              attributeEntries: nftAttributes.filter(({ traitType, value }) =>
                values.selectedNftAttributes.has(`${traitType}_${value}`)
              ),
            },
          };
      }

      if (editingRule) {
        // Editing: send the saved holder requirement back unchanged so the
        // server matches this exact discount and updates it in place.
        rule = {
          restrictionType: editingRule.restrictionType,
          restriction: JSON.parse(JSON.stringify(editingRule.restriction)),
        } as Partial<CampaignShopifyRule>;
      }

      if (rule.restriction) {
        // parse discount value only if there is a restriction
        const { discountValue, discountType } = formik.values;
        if (discountType === DiscountType.Amount) {
          // TODO: deal with more than 2 decimal values
          rule.discountAmount = discountValue;
          rule.discountPercent = 0;
        } else if (discountType === DiscountType.Percent) {
          // only accept ints
          rule.discountPercent = parseInt(discountValue.toString());
        }
      }

      if (rule.restriction) {
        rule!.restriction.tokenAddress = rule!.restriction?.tokenAddress.toLowerCase();
      }

      // Optional per-discount limits. "Same as product gate" sends null dates so
      // the discount keeps following the gate if the gate's dates change later.
      rule.maxRedemptions = values.capEnabled ? parseInt(values.maxRedemptions, 10) : null;
      if (values.datesMode === 'custom') {
        rule.startDate = new Date(values.customStart).toISOString();
        rule.endDate = new Date(values.customEnd).toISOString();
      } else {
        rule.startDate = null;
        rule.endDate = null;
      }

      try {
        const res = await campaignAPI.addCampaignRule(campaign!.id, selectedProductId, rule!);
        console.log('NewCampaignRuleResponse: ', res);
        await queryClient.invalidateQueries(['campaigns', campaign!.id.toString(), 'rules']);
        await queryClient.invalidateQueries(['campaigns', campaign!.id.toString()]);
        setToastMessage(isEditing ? 'Saved discount' : 'Added new rule');
        history.push(`/shopify/${appName}/campaigns/${campaignId}`);
      } catch (e) {
        setToastMessage(
          `There was an error ${isEditing ? 'saving' : 'creating'} this campaign rule. Received "${
            (e as Error).message
          }"`,
          true
        );
      }

      setSubmitting(false);
    },
  });

  const { nftAttributes, isLoading: isNFTAttributesLoading } = useFetchCampaignTokenNFTAttributes(
    parseInt(formik.values.network),
    formik.values.contractAddress
  );
  const refreshTokenMetadataMutation = useRefreshTokenMetadataMutation();

  // for discount types
  const [discountMoreInfoOpen, setDiscountMoreInfoOpen] = useState(false);

  const isSharedContract = useCallback(
    (networkId: number, contractAddress: string): string | '' => {
      const lowerCaseAddress = contractAddress.toLowerCase();
      const sharedStorefronts = getStorefrontsForNetwork(networkId);
      if (lowerCaseAddress in sharedStorefronts.map((x) => x.address)) {
        return sharedStorefronts.find((x) => x.address === lowerCaseAddress)!.name;
      }
      return '';
    },
    []
  );

  const handleToggleDiscountMoreInfoOpen = useCallback(
    () => setDiscountMoreInfoOpen((open) => !open),
    []
  );

  if (isLoading || !campaign) {
    return <SkeletonShopifyCampaign />;
  }

  // helper render functions
  /** Returns the controlled radio buttons for percentage/$ amount (discount type selector) */
  function renderDiscountType(): JSX.Element {
    return (
      <LegacyStack alignment="center">
        <span>&nbsp;</span>
        <RadioButton
          label="%"
          id="percent"
          name="discountType"
          onChange={() => {
            changeDiscountType(DiscountType.Percent);
          }}
          checked={formik.values.discountType === DiscountType.Percent}
        />
        <RadioButton
          label="$"
          id="amount"
          name="discountType"
          onChange={() => {
            changeDiscountType(DiscountType.Amount);
          }}
          checked={formik.values.discountType === DiscountType.Amount}
        />
      </LegacyStack>
    );
  }

  /** returns the discount value input box and the discount type selector */
  function renderDiscountInput(): JSX.Element {
    return (
      <LegacyStack vertical alignment="leading">
        <TextField
          autoComplete="off"
          label="Discount Value"
          value={formik.values.discountValue.toString(10)}
          onChange={(val) => {
            formik.setFieldTouched('discountValue', true);
            // TODO: parse to 2 decimal points depending on discount type
            formik.setFieldValue('discountValue', parseFloat(val));
          }}
          error={formik.errors.discountValue}
          type="number"
          requiredIndicator
          min={0}
          connectedRight={renderDiscountType()}
        />
        {formik.values.discountValue === 0 && formik.touched.discountValue && (
          <ExceptionList
            items={[
              {
                icon: AlertTriangleIcon,
                status: 'critical',
                description: `WARNING: For a $0/0% discount product please make sure your store-level inventory
              is set to 0, otherwise anyone could utilize the product ID to construct
              a purchase URL and create an order. Also deselect "Track quantity"
              for item inventory settings or product will display as SOLD OUT.
              Inventory tracking/limiting for NFT-exclusive products can be done in the
              "Products" section of your campaign if desired.`,
              },
            ]}
          />
        )}
        <Button
          variant="plain"
          onClick={handleToggleDiscountMoreInfoOpen}
          ariaExpanded={discountMoreInfoOpen}
          ariaControls="discount-more-info-collapsible"
          disclosure={discountMoreInfoOpen ? 'up' : 'down'}
        >
          {discountMoreInfoOpen ? 'Show less' : 'Show more'}
        </Button>
        <Collapsible
          open={discountMoreInfoOpen}
          id="discount-more-info-collapsible"
          transition={{ duration: '500ms', timingFunction: 'ease-in-out' }}
          expandOnPrint
        >
          <Text as="span" tone="subdued">
            <TextContainer spacing="tight">
              <p>
                A discount can be formatted in percent or in amount. If your item is available to
                holders, we recommend setting the product's original price to a high price point
                (e.g.: $10,000) and offering a discount to your desired price.
              </p>
              <p>
                This ensures the integrity of your gate in the off chance a hacker injects your
                product into their cart &mdash; they would have to pay you the original price to
                successfully create an order!
              </p>
            </TextContainer>
          </Text>
        </Collapsible>
      </LegacyStack>
    );
  }

  /** Optional cap + dates for this discount. Both default to following the gate. */
  function renderDiscountLimits(): JSX.Element {
    // Progressive disclosure: both settings are off by default and show their
    // inputs only once ticked. Off = the discount follows the product gate.
    return (
      <LegacyStack vertical spacing="tight">
        <Checkbox
          label="Set a cap for this discount"
          helpText={formik.values.capEnabled ? undefined : 'No limit'}
          checked={formik.values.capEnabled}
          onChange={(checked) => formik.setFieldValue('capEnabled', checked)}
        />
        {formik.values.capEnabled && (
          <div style={{ paddingLeft: '2.8rem', maxWidth: '20rem' }}>
            <TextField
              autoComplete="off"
              label="Max items at this discount"
              type="number"
              min={1}
              value={formik.values.maxRedemptions}
              onChange={(val) => formik.setFieldValue('maxRedemptions', val)}
              error={formik.errors.maxRedemptions}
            />
          </div>
        )}
        <Checkbox
          label="Set custom dates for this discount"
          helpText={
            formik.values.datesMode === 'custom'
              ? undefined
              : `${SAME_AS_GATE}: ${formatRange(gateStart || null, gateEnd || null)}`
          }
          checked={formik.values.datesMode === 'custom'}
          onChange={(checked) => {
            if (!checked) {
              formik.setFieldValue('datesMode', 'gate');
              return;
            }
            // Pre-fill with the gate's dates so only the end you need changes
            formik.setValues({
              ...formik.values,
              datesMode: 'custom',
              customStart: formik.values.customStart || gateStart || Date.now(),
              customEnd: formik.values.customEnd || gateEnd || (gateStart || Date.now()) + 86400000,
            });
          }}
        />
        {formik.values.datesMode === 'custom' && (
          <div style={{ paddingLeft: '2.8rem' }}>
            <LegacyStack distribution="fillEvenly">
              <ShopifyDateTimePicker
                label="Starts"
                value={formik.values.customStart}
                onChange={(val) => formik.setFieldValue('customStart', val)}
                error={formik.errors.customStart}
              />
              <ShopifyDateTimePicker
                label="Ends"
                value={formik.values.customEnd}
                onChange={(val) => formik.setFieldValue('customEnd', val)}
                error={formik.errors.customEnd}
              />
            </LegacyStack>
          </div>
        )}
      </LegacyStack>
    );
  }

  /** Edit mode: who qualifies is fixed — changing it would be a different discount. */
  function renderLockedHolders(): JSX.Element {
    const product = shopifyProducts.find((p) => String(p.productId) === String(editProductId));
    const r = editingRule!;
    let who = '';
    switch (r.restrictionType) {
      case CampaignShopifyRuleRestrictionType.TOKEN:
        who = 'Any token in the collection';
        break;
      case CampaignShopifyRuleRestrictionType.INDIVIDUAL_TOKEN:
        who = `Token #${r.restriction.tokenId}`;
        break;
      case CampaignShopifyRuleRestrictionType.TOKEN_RANGE:
        who = `Token IDs ${r.restriction.minTokenId} – ${r.restriction.maxTokenId}`;
        break;
      case CampaignShopifyRuleRestrictionType.TOKEN_LIST:
        who = `${formatTokenList(r.restriction.tokenIds)} (one shared discount)`;
        break;
      case CampaignShopifyRuleRestrictionType.TOKEN_ATTRIBUTE:
        who = r.restriction.attributeEntries.map((a) => `${a.traitType}: ${a.value}`).join(', ');
        break;
    }
    return (
      <LegacyStack vertical>
        {product && (
          <Text as="h2" variant="headingMd">
            {product.productData.title}
          </Text>
        )}
        <TextContainer spacing="tight">
          <p>
            <Text as="span" fontWeight="semibold">
              Contract{' '}
            </Text>
            <InlineCode>{r.restriction?.tokenAddress}</InlineCode>
          </p>
          <p>
            <Text as="span" fontWeight="semibold">
              Who qualifies{' '}
            </Text>
            {who}
          </p>
        </TextContainer>
        <Text as="span" tone="subdued">
          To change who qualifies, delete this rule and add a new one.
        </Text>
      </LegacyStack>
    );
  }

  function renderSharedContractWarning(): JSX.Element | null {
    const sharedStorefrontName = isSharedContract(
      parseInt(formik.values.network),
      formik.values.contractAddress
    );
    if (!sharedStorefrontName) {
      return null;
    }
    return (
      <ExceptionList
        items={[
          {
            icon: AlertCircleIcon,
            status: 'warning',
            description: `Rule with a shared storefront contract (current: ${sharedStorefrontName}) must be limited by token id (individual or range)`,
          },
        ]}
      />
    );
  }

  function renderFilterTypeDetailSelectors(): JSX.Element | null {
    switch (formik.values.filterType) {
      case 'id':
        return (
          <TextField
            autoComplete="off"
            label="Token ID"
            name="tokenId"
            value={formik.values.tokenId}
            onChange={(val) => {
              formik.setFieldValue('tokenId', val.trim());
            }}
            error={formik.errors.tokenId}
            requiredIndicator
          />
        );
      case 'id_list': {
        const { ids } = parseTokenIdList(formik.values.tokenIdList);
        return (
          <TextField
            autoComplete="off"
            label="Token IDs"
            name="tokenIdList"
            placeholder="15, 17, 25"
            value={formik.values.tokenIdList}
            onChange={(val) => formik.setFieldValue('tokenIdList', val)}
            error={formik.errors.tokenIdList}
            helpText={
              ids.length > 1
                ? `${ids.length} tokens share this discount${
                    formik.values.capEnabled ? ' and its cap' : ''
                  }.`
                : 'Separate with commas. All listed tokens share this discount and its cap.'
            }
            requiredIndicator
          />
        );
      }
      case 'id_range':
        return (
          <LegacyStack vertical>
            <TextField
              autoComplete="off"
              label="Min Token ID"
              name="minTokenId"
              value={formik.values.minTokenId}
              onChange={(val) => {
                formik.setFieldValue('minTokenId', val);
              }}
              error={formik.errors.minTokenId}
              requiredIndicator
              type="number"
            />
            <TextField
              autoComplete="off"
              label="Max Token ID"
              name="maxTokenId"
              value={formik.values.maxTokenId}
              onChange={(val) => {
                formik.setFieldValue('maxTokenId', val);
              }}
              error={formik.errors.maxTokenId}
              requiredIndicator
              type="number"
            />
          </LegacyStack>
        );
      case 'attributes':
        if (isSharedContract(parseInt(formik.values.network), formik.values.contractAddress)) {
          return null;
        }
        const toggleAttribute = (attributeId: string) => {
          const newSelections = new Set(formik.values.selectedNftAttributes);

          if (formik.values.selectedNftAttributes.has(attributeId)) {
            newSelections.delete(attributeId);
          } else {
            newSelections.add(attributeId);
          }

          formik.setFieldValue('selectedNftAttributes', newSelections);
        };
        const filterRegex = new RegExp(formik.values.searchAttributesInput, 'i');
        const removeTag = ({ traitType, value }: { traitType: string; value: string }) => {
          toggleAttribute(`${traitType}_${value}`);
        };

        return (
          <LegacyStack vertical>
            <Combobox
              preferredPosition="above"
              allowMultiple
              activator={
                <Combobox.TextField
                  autoComplete="off"
                  prefix={
                    isNFTAttributesLoading ? (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Spinner size="small" />
                      </div>
                    ) : (
                      <Icon source={nftAttributes.length ? SearchIcon : AlertCircleIcon} />
                    )
                  }
                  onChange={(val) => {
                    formik.setFieldValue('searchAttributesInput', val);
                  }}
                  label="Search attributes"
                  labelHidden
                  value={formik.values.searchAttributesInput}
                  placeholder="Search attributes"
                  disabled={!formik.values.contractAddress}
                  error={formik.values.contractAddress && formik.errors.searchAttributesInput}
                />
              }
            >
              {Boolean(formik.values.contractAddress) ? (
                <Listbox onSelect={(attributeId) => toggleAttribute(attributeId)}>
                  {isNFTAttributesLoading ? (
                    <Listbox.Loading accessibilityLabel="NFT Attributes Loading" />
                  ) : !nftAttributes.length ? (
                    <div
                      style={{
                        padding: '1rem',
                        display: 'flex',
                        flexFlow: 'column',
                        gap: '1rem',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Icon source={SearchRecentIcon} tone="base" />
                      <p color="var(--p-text)">No attributes found</p>
                    </div>
                  ) : (
                    nftAttributes
                      .filter(({ traitType, value }) => {
                        if (!formik.values.searchAttributesInput) {
                          return true;
                        }

                        const label = `${traitType}: ${value}`;
                        return label.match(filterRegex);
                      })
                      .map(({ traitType, value }) => {
                        const id = `${traitType}_${value}`;
                        const label = `${traitType}: ${value}`;
                        return (
                          <Listbox.Option
                            key={id}
                            value={id}
                            accessibilityLabel={label}
                            selected={formik.values.selectedNftAttributes.has(id)}
                          >
                            {label}
                          </Listbox.Option>
                        );
                      })
                  )}
                </Listbox>
              ) : null}
            </Combobox>
            {!formik.values.contractAddress && (
              <ExceptionList
                items={[
                  {
                    icon: AlertTriangleIcon,
                    status: 'warning',
                    description: `Enter contract address first.`,
                  },
                ]}
              />
            )}
            <TextContainer>
              <LegacyStack>
                {nftAttributes
                  .filter((attr) =>
                    formik.values.selectedNftAttributes.has(`${attr.traitType}_${attr.value}`)
                  )
                  .map((attr) => {
                    const label = `${attr.traitType}: ${attr.value}`;

                    return (
                      <Tag key={label} onRemove={() => removeTag(attr)}>
                        {label}
                      </Tag>
                    );
                  })}
              </LegacyStack>
            </TextContainer>
            {Boolean(formik.values.contractAddress) && (
              <Banner
                title="Not seeing attributes you expect?"
                tone="info"
                action={{
                  content: 'Refresh metadata',
                  loading: refreshTokenMetadataMutation.isLoading,
                  onAction: async () => {
                    try {
                      await refreshTokenMetadataMutation.mutateAsync({
                        network: parseInt(formik.values.network),
                        tokenAddress: formik.values.contractAddress,
                      });
                      setToastMessage('Triggered refresh. Check again in a few minutes.');
                    } catch (e) {
                      setToastMessage(`Unable to refresh metadata. Received: "${e}"`, true);
                    }
                  },
                }}
              >
                <p>Click here to refresh token attributes (may take a while)</p>
              </Banner>
            )}
          </LegacyStack>
        );
    }
    return null;
  }

  return (
    <>
      <Button icon={ChevronLeftIcon} url={`/shopify/${appName}/campaigns/${campaignId}`}></Button>

      <Page title={`${campaign.name} - ${isEditing ? 'Edit discount' : 'Manage Rules'}`}>
        <TitleBar
          title="rules"
          breadcrumbs={[
            {
              content: 'Product Gates',
              url: `/shopify/${appName}/campaigns`,
            },
            {
              content: campaignId,
              url: `/shopify/${appName}/campaigns/${campaignId}`,
            },
          ]}
          primaryAction={{ content: 'New Product Gate', url: `/shopify/${appName}/campaigns/new` }}
        />
        <Layout>
          <Layout.Section>
            <LegacyCard
              primaryFooterAction={{
                content: isEditing ? 'Save' : 'Add',
                disabled: !formik.dirty || !formik.isValid,
                onAction: () => {
                  formik.submitForm();
                },
                loading: formik.isSubmitting,
              }}
            >
              <LegacyCard.Section>
                {isEditing ? (
                  <LegacyCard.Subsection>{renderLockedHolders()}</LegacyCard.Subsection>
                ) : (
                  <>
                    <LegacyCard.Subsection>
                      <LegacyStack vertical>
                        {shopifyProducts.length === 1 ? (
                          <Text as="h2" variant="headingMd">
                            {shopifyProducts[0].productData.title}
                          </Text>
                        ) : (
                          <Select
                            requiredIndicator
                            label="Product"
                            options={[
                              {
                                value: '',
                                label: 'Select...',
                                disabled: true,
                                prefix: isShopifyProductsLoading ? (
                                  <div
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                    }}
                                  >
                                    <Spinner size="small" />
                                  </div>
                                ) : (
                                  <Icon source={ProductIcon} />
                                ),
                              },
                              ...shopifyProducts.map((product) => {
                                return {
                                  value: product.productId.toString(),
                                  label: product.productData.title,
                                };
                              }),
                            ]}
                            value={formik.values.selectedProductId}
                            onChange={(val) => {
                              formik.setFieldValue('selectedProductId', val);
                            }}
                            error={formik.errors.selectedProductId}
                          />
                        )}
                      </LegacyStack>
                    </LegacyCard.Subsection>
                    <LegacyCard.Subsection>
                      <LegacyStack vertical>
                        <Select
                          requiredIndicator
                          label="Network"
                          options={[
                            {
                              value: '',
                              label: 'Select...',
                              disabled: true,
                              prefix: <Icon source={ProductIcon} />,
                            },
                            ...networks.map((network) => {
                              return {
                                value: network.value,
                                label: network.name,
                              };
                            }),
                          ]}
                          value={`${formik.values.network}`}
                          onChange={(val) => {
                            formik.setFieldValue('network', val);
                          }}
                          error={formik.errors.network}
                        />
                        <TextField
                          autoComplete="off"
                          label="Contract address"
                          placeholder="0x123abc..."
                          value={formik.values.contractAddress}
                          onChange={(val) => {
                            formik.setFieldValue('contractAddress', val);
                          }}
                          error={formik.errors.contractAddress}
                          requiredIndicator
                        />
                        {renderSharedContractWarning()}
                        <LegacyStack vertical>
                          <LegacyStack vertical>
                            <RadioButton
                              label="All tokens"
                              helpText="Include holders of any token in a given collection in your allowlist."
                              id="no_filter"
                              name="no_filter"
                              onChange={(checked) => {
                                if (checked) {
                                  formik.setFieldValue('filterType', 'no_filter');
                                }
                              }}
                              checked={formik.values.filterType === 'no_filter'}
                              disabled={
                                isSharedContract(
                                  parseInt(formik.values.network),
                                  formik.values.contractAddress
                                ) !== ''
                              }
                            />
                            <RadioButton
                              label="Select individual token"
                              helpText="Choose individual token to allowlist for this campaign."
                              id="id"
                              name="id"
                              onChange={(checked) => {
                                if (checked) {
                                  formik.setFieldValue('filterType', 'id');
                                }
                              }}
                              checked={formik.values.filterType === 'id'}
                            />
                            <RadioButton
                              label="Select a list of tokens"
                              helpText="Several token IDs that share one discount and one cap."
                              id="id_list"
                              name="id_list"
                              onChange={(checked) => {
                                if (checked) {
                                  formik.setFieldValue('filterType', 'id_list');
                                }
                              }}
                              checked={formik.values.filterType === 'id_list'}
                            />
                            <RadioButton
                              label="Filter by token id range"
                              helpText="Include holders of a given collection in your allowlist only if their token IDs fall in a certain range."
                              id="id_range"
                              name="id_range"
                              onChange={(checked) => {
                                if (checked) {
                                  formik.setFieldValue('filterType', 'id_range');
                                }
                              }}
                              checked={formik.values.filterType === 'id_range'}
                              disabled={
                                isSharedContract(
                                  parseInt(formik.values.network),
                                  formik.values.contractAddress
                                ) !== ''
                              }
                            />
                            <RadioButton
                              label="Filter by attribute"
                              helpText="Include holders of tokens with a certain metadata attribute onto your allowlist"
                              id="attributes"
                              name="attributes"
                              onChange={(checked) => {
                                if (checked) {
                                  formik.setFieldValue('filterType', 'attributes');
                                }
                              }}
                              checked={formik.values.filterType === 'attributes'}
                              disabled={
                                isSharedContract(
                                  parseInt(formik.values.network),
                                  formik.values.contractAddress
                                ) !== ''
                              }
                            />
                            {formik.errors.filterType && (
                              <ExceptionList
                                items={[
                                  {
                                    icon: AlertTriangleIcon,
                                    status: 'critical',
                                    description: formik.errors.filterType,
                                  },
                                ]}
                              />
                            )}
                          </LegacyStack>
                          {renderFilterTypeDetailSelectors()}
                        </LegacyStack>
                      </LegacyStack>
                    </LegacyCard.Subsection>
                  </>
                )}
                <LegacyCard.Subsection>{renderDiscountInput()}</LegacyCard.Subsection>
                <LegacyCard.Subsection>{renderDiscountLimits()}</LegacyCard.Subsection>
              </LegacyCard.Section>
            </LegacyCard>
          </Layout.Section>
        </Layout>
      </Page>
    </>
  );
};
